import { OperationGroup, OperationGroupCreate, Pagination } from '@ekkogmbh/apisdk';
import { Add, Delete, Description } from '@mui/icons-material';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { GenericDialog } from '../../Common/Components/GenericDialog';
import { DataTable, dataTableHighlight } from '../../Common/Components/DataTable';
import Permissions from 'src/Common/Permissions';
import { OperationGroupPanel } from './OperationGroupPanel';
import { ContentControl } from 'src/Common/Components/ContentControl';
import { ContentControlButton } from 'src/Common/Components/ContentControl/ContentControlButton';
import { nodeSeparator } from 'src/AreaManagement/Components/AreaManagementContent';
import useEkko from 'src/Common/Providers/EkkoProvider';
import { useContentControl } from 'src/Common/Stores/ContentControlProvider';
import useRequest from 'src/Common/Hooks/useRequest';
import usePagination from 'src/Common/Providers/PaginationProvider';
import ScrollableStack from 'src/Common/Components/ScrollableStack';

const ROW_MIN_HEIGHT = 48;

export const OperationGroupsContent = (): React.JSX.Element => {
  const { api, hasPermission } = useEkko();
  const { refresh } = usePagination();
  const { setPanelOpen } = useContentControl();
  const navigate = useNavigate();

  const [, startLoading] = useRequest();
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [selectedGroup, setSelectedGroup] = useState<OperationGroup>();

  const dismiss = useCallback(() => {
    setPanelOpen(false);
    setDialogOpen(false);
    setSelectedGroup(undefined);
  }, []);

  const deleteGroup = useCallback(() => {
    if (selectedGroup === undefined) {
      return;
    }
    startLoading(
      () => api.deleteOperationGroup(selectedGroup),
      () => {
        dismiss();
        refresh();
      },
    );
  }, [api, selectedGroup]);

  const addGroup = useCallback(
    (payload: OperationGroupCreate, csv?: File) => {
      startLoading(
        () => api.addOperationGroup(payload, csv),
        () => refresh(),
      );
    },
    [api],
  );

  const hasWritePermission = useMemo(() => hasPermission(Permissions.OPERATION_GROUPS_WRITE), [hasPermission]);

  const fetchItems = useCallback((pagination: Pagination) => api.getOperationGroups(pagination), [api]);

  return (
    <ContentControl
      panel={<OperationGroupPanel saveHandler={addGroup} closeHandler={dismiss} />}
      dialog={
        <GenericDialog
          type="confirmation"
          maxWidth={'sm'}
          fullWidth={true}
          centered={true}
          open={isDialogOpen}
          title={'Delete Operation-Group'}
          text={
            <div>
              Delete Operation-Group: <span style={{ fontWeight: 700, fontSize: 14 }}>{selectedGroup?.name ?? ''}</span>
            </div>
          }
          onClose={dismiss}
          onConfirm={deleteGroup}
        />
      }
      buttons={[
        <ContentControlButton
          key={'add'}
          content={<Add />}
          tooltip={'Add Operation-Group'}
          onClick={() => {
            dismiss();
            setPanelOpen(true);
          }}
          requiredPermission={Permissions.OPERATION_GROUPS_WRITE}
        />,
      ]}
    >
      <DataTable
        fetchItems={fetchItems}
        columns={[
          {
            header: 'Name',
            body: (entry: OperationGroup, searchText?: string) => (
              <ScrollableStack direction="row" width="100%" minHeight={ROW_MIN_HEIGHT} alignItems="center">
                {searchText ? dataTableHighlight(entry.name ?? '', [searchText]) : entry.name}
              </ScrollableStack>
            ),
            filterField: 'name',
            sortField: 'OG.name',
            proportionalContent: '25%',
          },
          {
            header: 'Description',
            body: (entry: OperationGroup, searchText?: string) => (
              <ScrollableStack direction="row" width="100%" minHeight={ROW_MIN_HEIGHT} alignItems="center">
                {searchText ? dataTableHighlight(entry.description ?? '', [searchText]) : entry.description}
              </ScrollableStack>
            ),
            filterField: 'description',
            sortField: 'OG.description',
            proportionalContent: '25%',
          },
          {
            header: 'Area',
            body: (entry: OperationGroup, searchText?: string) => (
              <ScrollableStack direction="row" width="100%" minHeight={ROW_MIN_HEIGHT} alignItems="center">
                <span style={{ whiteSpace: 'nowrap' }}>
                  {searchText
                    ? dataTableHighlight(entry.nodeParts.join(nodeSeparator), [searchText])
                    : entry.nodeParts.join(nodeSeparator)}
                </span>
              </ScrollableStack>
            ),
            proportionalContent: '50%',
          },
          {
            header: 'State',
            body: (entry: OperationGroup) => entry.state,
            filterField: 'state',
            sortField: 'OG.state',
            fitContent: true,
          },
          {
            header: 'Created-At',
            body: (entry: OperationGroup) => {
              const value = entry.createdAt;
              const dateString = new Date(value).toLocaleDateString();
              const timeString = new Date(value).toLocaleTimeString();
              return (
                <div>
                  {dateString} - {timeString}
                </div>
              );
            },
            filterField: 'createdAt',
            sortField: 'OG.createdAt',
            searchTooltip: 'Format: YYYY-MM-DD',
            fitContent: true,
          },
        ]}
        actions={[
          {
            title: 'Details',
            icon: <Description />,
            onClick: (entry: OperationGroup) => {
              navigate('/operationgroups/' + String(entry.id));
            },
            disabled: !hasWritePermission,
          },
          {
            title: 'Delete',
            icon: <Delete />,
            onClick: (entry: OperationGroup) => {
              dismiss();
              setSelectedGroup(entry);
              setDialogOpen(true);
            },
            disabled: !hasWritePermission,
          },
        ]}
      />
    </ContentControl>
  );
};
